<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		AdvancedCardV2,
		Container,
		DashDeviceWidget,
		DeviceButtonV2,
		LabelListV2,
		LabelV2,
		Modal,
		PreviewBox,
		Text,
	} from '@Shelly/shelly-design-system';

	export let showModal = false;
	export let is1L = false;

	$: onChip = $format('input_state_chip_on', { default: 'Input: On' });
	$: offChip = $format('input_state_chip_off', { default: 'Input: Off' });
	$: inputOnLabel = $format('prop_input', {
		default: 'Input ({value})',
		values: { value: 0 },
	});
	$: inputOffLabel = $format('prop_input', {
		default: 'Input ({value})',
		values: { value: 1 },
	});
	$: onText = $format('xt1_template_on', { default: 'On' });
	$: offText = $format('xt1_template_off', { default: 'Off' });
	$: deviceName = $format('label_living_room', { default: 'Living room' });
	$: previewOn = $format('input_state_preview_on', { default: 'Physical input on' });
	$: previewOff = $format('input_state_preview_off', { default: 'Physical input off' });

	const previewAvatar = {
		icon: 'fal fa-lightbulb',
		backgroundColor: 'color-base',
	};
</script>

<Modal
	bind:showModal
	title={$format('title_input_state_preview', { default: 'Input state preview' })}
	on:close={() => {
		showModal = false;
	}}
>
	<Container slot="content" padding="0">
		<PreviewBox backgroundImageSrc="./images/loginBackground.png" minHeight={220} padding="m">
			<div class="input-state-preview-grid pointer-events-none width-full">
				{#if is1L}
					<div class="input-state-preview-heading">
						<Text styleType="body-small" class="text-center text-white">
							{$format('input_state_preview_heading_labels', {
								default: 'On device status labels',
							})}
						</Text>
					</div>
					<div class="input-state-preview-span d-flex justify-content-center">
						<LabelListV2
							size="m"
							a11yProps={{ isInteractive: false }}
							labelList={[
								{ value: `${inputOnLabel}: ${onText}`, icon: 'fal fa-plug' },
								{ value: `${inputOffLabel}: ${offText}`, icon: 'fal fa-plug' },
							]}
						/>
					</div>
				{:else}
					<div class="input-state-preview-heading">
						<Text styleType="body-small" class="text-center text-white">
							{$format('input_state_preview_heading_button', {
								default: 'On the power button',
							})}
						</Text>
					</div>
					<div class="d-flex flex-direction-column gap-s">
						<Text styleType="tiny" class="text-white">{previewOn}</Text>
						<AdvancedCardV2
							title={deviceName}
							avatar={previewAvatar}
							previewMode
							hideSlotMain
							hideSlotStatusBar
							isFocusable={false}
						>
							<svelte:fragment slot="right">
								<DeviceButtonV2
									id="input-state-preview-on"
									variant="primary"
									icon="fa fa-power-off"
									isActive
									indicator="on"
									size="m"
								/>
							</svelte:fragment>
						</AdvancedCardV2>
					</div>
					<div class="d-flex flex-direction-column gap-s">
						<Text styleType="tiny" class="text-white">{previewOff}</Text>
						<AdvancedCardV2
							title={deviceName}
							avatar={previewAvatar}
							previewMode
							hideSlotMain
							hideSlotStatusBar
							isFocusable={false}
						>
							<svelte:fragment slot="right">
								<DeviceButtonV2
									id="input-state-preview-off"
									variant="primary"
									icon="fa fa-power-off"
									isActive={false}
									indicator="off"
									size="m"
								/>
							</svelte:fragment>
						</AdvancedCardV2>
					</div>
					<div class="input-state-preview-heading">
						<Text styleType="body-small" class="text-center text-white">
							{$format('input_state_preview_heading_card', {
								default: 'On the device card',
							})}
						</Text>
					</div>
					<div class="d-flex flex-direction-column gap-s">
						<Text styleType="tiny" class="text-white">{previewOn}</Text>
						<DashDeviceWidget
							value={deviceName}
							avatar={previewAvatar}
							active
							arrowRight
							hideSlotRight
							isFocusable={false}
							indicator={{ show: false, powerOn: false }}
							a11yProps={{
								indicatorOnAriaLabel: '',
								indicatorOffAriaLabel: '',
								arrowButtonAriaLabel: '',
							}}
						>
							<div slot="info" style="display: contents">
								<LabelV2
									size="m"
									value={onChip}
									icon="fal fa-plug"
									iconColor="--color-1"
									textClass="text-color-base"
									noBackground
									noPadding
								/>
							</div>
						</DashDeviceWidget>
					</div>
					<div class="d-flex flex-direction-column gap-s">
						<Text styleType="tiny" class="text-white">{previewOff}</Text>
						<DashDeviceWidget
							value={deviceName}
							avatar={previewAvatar}
							active={false}
							arrowRight
							hideSlotRight
							isFocusable={false}
							indicator={{ show: false, powerOn: false }}
							a11yProps={{
								indicatorOnAriaLabel: '',
								indicatorOffAriaLabel: '',
								arrowButtonAriaLabel: '',
							}}
						>
							<div slot="info" style="display: contents">
								<LabelV2
									size="m"
									value={offChip}
									icon="fal fa-plug"
									iconColor="--color-white"
									textClass="text-white"
									noBackground
									noPadding
								/>
							</div>
						</DashDeviceWidget>
					</div>
				{/if}
			</div>
		</PreviewBox>
	</Container>
</Modal>

<style>
	.input-state-preview-grid {
		display: grid;
		grid-template-columns: 1fr;
		gap: 16px;
	}

	.input-state-preview-heading,
	.input-state-preview-span {
		grid-column: 1 / -1;
		text-align: center;
	}

	@media (min-width: 640px) {
		.input-state-preview-grid {
			grid-template-columns: 1fr 1fr;
		}
	}
</style>
